/**
 * LiveKit transport wiring (iter135.18 — bi-directional audio).
 *
 * Wraps the small slice of livekit-client we need:
 *
 *   1. Mint an SFU token via our auth-gated backend endpoint.
 *   2. Connect to the LiveKit room.
 *   3. Presenter: publish screen video + system audio (when the
 *      browser grants it) + optional microphone.
 *   4. Viewer: subscribe to all remote tracks (video + audio) and
 *      surface them via callbacks so the UI can attach video to a
 *      ``<video>`` and audio to a hidden ``<audio>``. Viewers can
 *      also publish their own microphone (source-scoped grant on
 *      the JWT prevents them from publishing screen/camera).
 *
 * Provider-aware: if the backend returns 501 ``sfu_not_configured``,
 * we surface the failure via ``onError`` so the caller can fall back
 * to its placeholder UI.
 */
import api from "@/lib/api";
import {
  Room, RoomEvent, Track, LocalTrackPublication,
  createLocalAudioTrack,
} from "livekit-client";

// ── Token ──────────────────────────────────────────────────────────

/**
 * Mint an SFU token for an existing Share Access Token. Backend
 * validates the access token via §5 and may return 501 if SFU is
 * disabled; the caller should treat that as a graceful fallback,
 * not an error.
 */
export async function mintSfuToken(shareAccessToken) {
  const r = await api.post(
    `/conv/share/sfu/token?token=${encodeURIComponent(shareAccessToken)}`,
  );
  return r.data; // { token, url }
}

// ── Presenter ──────────────────────────────────────────────────────

/**
 * Connect as a Presenter, publish the captured screen stream
 * (video + optional system audio), and return a handle with a
 * ``disconnect``, ``publishMic`` and ``unpublishMic`` API. Caller
 * MUST call ``disconnect`` on unmount or when the share ends.
 */
export async function presenterConnect({
  shareAccessToken, mediaStream,
  onConnected, onDisconnected, onError,
  onActiveSpeakers,
}) {
  let mint;
  try {
    mint = await mintSfuToken(shareAccessToken);
  } catch (e) {
    // 501 sfu_not_configured → caller falls back to the local-preview
    // UI. Bubble the failure so it can decide.
    onError?.(e);
    throw e;
  }
  const room = new Room({
    adaptiveStream: true,
    dynacast: true,
    publishDefaults: {
      videoCodec: "vp8",
      simulcast: false,
      videoEncoding: { maxBitrate: 1_500_000, maxFramerate: 15 },
    },
  });
  room.on(RoomEvent.Disconnected, () => onDisconnected?.());
  if (onActiveSpeakers) {
    room.on(RoomEvent.ActiveSpeakersChanged, (speakers) => {
      onActiveSpeakers(speakers.map((p) => p.identity));
    });
  }
  await room.connect(mint.url, mint.token);

  // Track lifecycle. The live video track is source=ScreenShare.
  // Any audio tracks on the captured stream are system/tab audio
  // (NOT mic) and get source=ScreenShareAudio. This matches
  // LiveKit's built-in semantics so remote clients can filter.
  const publishedPubs = [];
  for (const track of mediaStream.getVideoTracks()) {
    const pub = await room.localParticipant.publishTrack(track, {
      source: Track.Source.ScreenShare,
      name: "screen",
    });
    publishedPubs.push(pub);
  }
  for (const track of mediaStream.getAudioTracks()) {
    const pub = await room.localParticipant.publishTrack(track, {
      source: Track.Source.ScreenShareAudio,
      name: "screen-audio",
      dtx: false,       // music-friendly
      red: true,
    });
    publishedPubs.push(pub);
  }

  // Mic publication state — held outside the initial publish list
  // so ``disconnect`` can clean it up whether or not the user
  // toggled it.
  let micTrack = null;
  let micPub = null;

  onConnected?.();
  return {
    room,
    /** Whether the captured screen stream carries audio — used by
     *  the UI to surface a "🔊 Sharing audio" pill. */
    hasSystemAudio: mediaStream.getAudioTracks().length > 0,
    publishMic: async () => {
      if (micTrack) return;
      micTrack = await createLocalAudioTrack({
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      });
      micPub = await room.localParticipant.publishTrack(micTrack, {
        source: Track.Source.Microphone,
        name: "mic",
      });
    },
    unpublishMic: async () => {
      if (!micTrack) return;
      try { await room.localParticipant.unpublishTrack(micTrack); } catch { /* noop */ }
      try { micTrack.stop(); } catch { /* noop */ }
      micTrack = null;
      micPub = null;
    },
    get micPublished() { return !!micPub; },
    disconnect: async () => {
      try {
        for (const pub of publishedPubs) {
          if (pub instanceof LocalTrackPublication && pub.track) {
            try { pub.track.stop(); } catch { /* noop */ }
          }
        }
        if (micTrack) {
          try { micTrack.stop(); } catch { /* noop */ }
        }
        await room.disconnect();
      } catch { /* noop */ }
    },
  };
}

// ── Viewer ─────────────────────────────────────────────────────────

/**
 * Connect to the LiveKit room as a Viewer.
 *
 *  Callbacks:
 *   - ``onVideoTrack(track, participant)`` — a remote video track
 *     was subscribed. The UI attaches it to a ``<video>`` element.
 *   - ``onAudioTrack(track, participant)`` — a remote audio track
 *     was subscribed. The UI mounts an invisible ``<audio>``
 *     element so the browser auto-plays the audio. Covers both
 *     presenter system audio AND other participants' microphones.
 *   - ``onTrackEnded(track, participant)`` — a previously attached
 *     track went away. The UI removes the corresponding media
 *     element.
 *
 *  Publish API: viewers get a mic-only publish grant so
 *  ``publishMic()`` / ``unpublishMic()`` behave identically to the
 *  presenter handle. Attempting to publish a video track will be
 *  rejected by the SFU.
 */
export async function viewerConnect({
  shareAccessToken,
  onConnected, onDisconnected, onError,
  onVideoTrack, onAudioTrack, onTrackEnded,
  onActiveSpeakers,
}) {
  let mint;
  try {
    mint = await mintSfuToken(shareAccessToken);
  } catch (e) {
    onError?.(e);
    throw e;
  }
  const room = new Room({ adaptiveStream: true, dynacast: true });

  // Surface every subscribed remote track — video and audio alike.
  // The UI decides which DOM element to mount each on.
  const dispatchTrack = (track, publication, participant) => {
    if (!track) return;
    if (track.kind === "video") onVideoTrack?.(track, participant);
    else if (track.kind === "audio") onAudioTrack?.(track, participant);
  };
  room.on(RoomEvent.TrackSubscribed, dispatchTrack);
  room.on(RoomEvent.TrackUnsubscribed,
    (track, publication, participant) => onTrackEnded?.(track, participant));
  room.on(RoomEvent.Disconnected, () => onDisconnected?.());
  if (onActiveSpeakers) {
    room.on(RoomEvent.ActiveSpeakersChanged, (speakers) => {
      onActiveSpeakers(speakers.map((p) => p.identity));
    });
  }

  await room.connect(mint.url, mint.token);

  // Attach any tracks that were already subscribed before our
  // listener registered (race on slow networks).
  for (const participant of room.remoteParticipants.values()) {
    for (const publication of participant.trackPublications.values()) {
      const t = publication.track;
      if (t) dispatchTrack(t, publication, participant);
      else if (!publication.isSubscribed) {
        try { publication.setSubscribed?.(true); } catch { /* noop */ }
      }
    }
  }

  let micTrack = null;
  let micPub = null;

  onConnected?.();
  return {
    room,
    publishMic: async () => {
      if (micTrack) return;
      micTrack = await createLocalAudioTrack({
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      });
      micPub = await room.localParticipant.publishTrack(micTrack, {
        source: Track.Source.Microphone,
        name: "mic",
      });
    },
    unpublishMic: async () => {
      if (!micTrack) return;
      try { await room.localParticipant.unpublishTrack(micTrack); } catch { /* noop */ }
      try { micTrack.stop(); } catch { /* noop */ }
      micTrack = null;
      micPub = null;
    },
    get micPublished() { return !!micPub; },
    disconnect: async () => {
      if (micTrack) {
        try { micTrack.stop(); } catch { /* noop */ }
      }
      try { await room.disconnect(); } catch { /* noop */ }
    },
  };
}
